import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface GrantApplication {
  id: string;
  user_id: string;
  grant_id: string;
  applied: boolean;
  applied_date?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export const useGrantApplications = (grantIds: string[] = []) => {
  const [applications, setApplications] = useState<GrantApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchApplications = async () => {
    if (grantIds.length === 0) return;

    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      const { data, error } = await supabase
        .from('user_grant_applications')
        .select('*')
        .eq('user_id', user.id)
        .in('grant_id', grantIds);

      if (error) throw error;
      
      setApplications(data || []);
    } catch (error) {
      console.error('Error fetching grant applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleApplication = async (grantId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication required",
          description: "Please sign in to track your grant applications.",
          variant: "destructive",
        });
        return false;
      }

      // Check if application record exists
      const existingApp = applications.find(app => app.grant_id === grantId);
      
      if (existingApp) {
        // Update existing record
        const newAppliedStatus = !existingApp.applied;
        const { error } = await supabase
          .from('user_grant_applications')
          .update({
            applied: newAppliedStatus,
            applied_date: newAppliedStatus ? new Date().toISOString() : null
          })
          .eq('id', existingApp.id);

        if (error) throw error;

        // Update local state
        setApplications(prev => 
          prev.map(app => 
            app.id === existingApp.id 
              ? { 
                  ...app, 
                  applied: newAppliedStatus,
                  applied_date: newAppliedStatus ? new Date().toISOString() : null
                }
              : app
          )
        );

        toast({
          title: newAppliedStatus ? "Application tracked!" : "Application removed",
          description: newAppliedStatus 
            ? "Grant application has been marked as applied."
            : "Grant application tracking has been removed.",
        });

        return true;
      } else {
        // Create new record
        const { data, error } = await supabase
          .from('user_grant_applications')
          .insert({
            user_id: user.id,
            grant_id: grantId,
            applied: true,
            applied_date: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;

        // Update local state
        setApplications(prev => [...prev, data]);

        toast({
          title: "Application tracked!",
          description: "Grant application has been marked as applied.",
        });

        return true;
      }
    } catch (error) {
      console.error('Error toggling grant application:', error);
      toast({
        title: "Error",
        description: "Failed to update application status. Please try again.",
        variant: "destructive",
      });
      return false;
    }
  };

  const isApplied = (grantId: string): boolean => {
    const app = applications.find(app => app.grant_id === grantId);
    return app?.applied || false;
  };

  const getApplicationDate = (grantId: string): string | null => {
    const app = applications.find(app => app.grant_id === grantId);
    return app?.applied_date || null;
  };

  useEffect(() => {
    fetchApplications();
  }, [grantIds.join(',')]);

  return {
    applications,
    loading,
    toggleApplication,
    isApplied,
    getApplicationDate,
    refetch: fetchApplications
  };
};